import {
  createMaterialTopTabNavigator,
  MaterialTopTabNavigationEventMap,
  MaterialTopTabNavigationOptions,
} from '@react-navigation/material-top-tabs'
import { ParamListBase, TabNavigationState } from '@react-navigation/native'
import { useLocalSearchParams, withLayoutContext } from 'expo-router'
import { Dimensions, View } from 'react-native'
import { SystemBars } from 'react-native-edge-to-edge'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import FosterActionButton from '@/components/fosters/FosterActionButton'
import { FosterFormProvider } from '@/components/fosters/FosterFormContext'
import TourSpot from '@/components/tour/TourSpot'
import UniIcon from '@/components/unistyles/UniIcon'
import UniText from '@/components/unistyles/UniText'
import { useAppStore } from '@/store/useAppStore'

const { Navigator } = createMaterialTopTabNavigator()

const emptyComponent = () => <></>

export const MaterialTopTabs = withLayoutContext<
  MaterialTopTabNavigationOptions,
  typeof Navigator,
  TabNavigationState<ParamListBase>,
  MaterialTopTabNavigationEventMap
>(Navigator)

const TAB_SPOT_IDS: Record<string, string> = {
  'information-outline': 'foster-profile-tab',
  needle: 'foster-health-tab',
  'heart-outline': 'foster-adoptions-tab',
  'file-document-multiple-outline': 'foster-documents-tab',
}

const renderTabBarIcon = (source: string, label: string) => {
  const spotId = TAB_SPOT_IDS[source]

  const TabBarIcon = (props: { focused: boolean; color: string }) => {
    const content = (
      <View
        // eslint-disable-next-line react-native/no-inline-styles
        style={{
          alignItems: 'center',
          gap: 8,
          justifyContent: 'center',
          minWidth: 50,
          width: '100%',
        }}
      >
        <UniIcon color={props.color} size={24} source={source} />
        <UniText
          maxFontSizeMultiplier={1.5}
          // eslint-disable-next-line react-native/no-inline-styles
          style={{
            color: props.color,
            fontSize: 10,
          }}
        >
          {label}
        </UniText>
      </View>
    )

    if (!spotId) return content

    return <TourSpot id={spotId}>{content}</TourSpot>
  }
  TabBarIcon.displayName = `TabBarIcon(${source})`
  return TabBarIcon
}

const TabLayout = () => {
  const { theme } = useUnistyles()
  const tabBarBottomInset = useSafeAreaInsets().bottom - 10

  const { id: fosterId } = useLocalSearchParams<{ id: string }>()

  const currentOrganization = useAppStore.use.currentOrganization().organization

  if (!currentOrganization?.id) {
    return null
  }

  return (
    <FosterFormProvider fosterId={fosterId} key={fosterId}>
      <MaterialTopTabs
        backBehavior="none"
        initialLayout={{
          width: Dimensions.get('window').width,
        }}
        screenOptions={{
          tabBarContentContainerStyle: {
            paddingTop: 5,
            paddingBottom: tabBarBottomInset,
          },
          tabBarShowLabel: false,
          animationEnabled: true,
          lazy: true,
          swipeEnabled: true,
          tabBarStyle: [
            styles.tabBarStyle,
            {
              elevation: 10,
            },
          ],
          tabBarIndicator: emptyComponent,
          sceneStyle: { backgroundColor: theme.colors.background },
        }}
        style={{ backgroundColor: theme.colors.background }}
        tabBarPosition="bottom"
      >
        <MaterialTopTabs.Screen
          initialParams={{ id: fosterId }}
          name="index"
          options={{
            tabBarAllowFontScaling: false,
            tabBarActiveTintColor: theme.colors.primary,
            tabBarInactiveTintColor: theme.colors.secondary,
            tabBarIcon: renderTabBarIcon('information-outline', 'Profile'),
            tabBarButtonTestID: 'profile-tab',
          }}
        />
        <MaterialTopTabs.Screen
          initialParams={{ id: fosterId }}
          name="health"
          options={{
            tabBarActiveTintColor: theme.colors.primary,
            tabBarInactiveTintColor: theme.colors.secondary,
            tabBarLabel: 'Health',
            tabBarIcon: renderTabBarIcon('needle', 'Health'),
            tabBarButtonTestID: 'health-tab',
          }}
        />
        <MaterialTopTabs.Screen
          initialParams={{ id: fosterId }}
          name="adoptions"
          options={{
            tabBarActiveTintColor: theme.colors.primary,
            tabBarInactiveTintColor: theme.colors.secondary,
            tabBarLabel: 'Adoption',
            tabBarIcon: renderTabBarIcon('heart-outline', 'Adoption'),
            tabBarButtonTestID: 'adoptions-tab',
          }}
        />
        <MaterialTopTabs.Screen
          initialParams={{ id: fosterId }}
          name="documents"
          options={{
            tabBarActiveTintColor: theme.colors.primary,
            tabBarInactiveTintColor: theme.colors.secondary,
            tabBarLabel: 'Documents',
            tabBarIcon: renderTabBarIcon(
              'file-document-multiple-outline',
              'Documents'
            ),
            tabBarButtonTestID: 'documents-tab',
          }}
        />
      </MaterialTopTabs>
      <FosterActionButton
        fosterId={fosterId}
        style={{
          paddingRight: 10,
          paddingBottom: 76 + tabBarBottomInset,
        }}
      />

      <SystemBars style="auto" />
    </FosterFormProvider>
  )
}

const styles = StyleSheet.create((theme) => ({
  tabBarStyle: {
    backgroundColor: theme.colors.surface,
    elevation: 8,
    shadowColor: theme.colors.shadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: theme.tokens.spacing[2],
  },
}))

export default TabLayout
